<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    hide-overlay
    transition="dialog-bottom-transition"
    content-class="vision-camera-dialog"
    @input="handleDialogInput"
  >
    <div class="vision-camera vision-scope">
      <v-btn icon class="camera-close" :aria-label="'Fechar'" @click="close">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>

      <section v-if="step === 'start'" class="camera-start">
        <div class="camera-start__panel">
          <div class="camera-start__icon">
            <v-icon size="36" color="white">{{ isAiMode ? "mdi-image-search-outline" : "mdi-camera-iris" }}</v-icon>
          </div>
          <span class="camera-start__eyebrow">{{ isAiMode ? "Captura com IA" : "Captura manual" }}</span>
          <h2>{{ title }}</h2>
          <p>{{ startText }}</p>
          <v-btn class="camera-start__button" :loading="starting" large @click="startCamera">
            <v-icon left>mdi-camera-outline</v-icon>
            Abrir câmera
          </v-btn>

          <v-alert v-if="errorMessage" dense text type="error" class="camera-start__error">
            {{ errorMessage }}
          </v-alert>
        </div>
      </section>

      <section v-show="step === 'camera'" class="camera-stage">
        <video ref="video" autoplay playsinline muted></video>

        <svg class="mask-overlay" :width="mask.screenW" :height="mask.screenH">
          <defs>
            <mask :id="maskId">
              <rect fill="white" x="0" y="0" :width="mask.screenW" :height="mask.screenH" />
              <rect fill="black" :x="mask.x" :y="mask.y" :width="mask.width" :height="mask.height" :rx="mask.rx" :ry="mask.rx" />
            </mask>
          </defs>
          <rect fill="rgba(0,0,0,0.62)" x="0" y="0" :width="mask.screenW" :height="mask.screenH" :mask="`url(#${maskId})`" />
          <rect fill="none" stroke="rgba(255,255,255,0.85)" stroke-width="2" :x="mask.x" :y="mask.y" :width="mask.width" :height="mask.height" :rx="mask.rx" :ry="mask.rx" />
          <rect fill="none" stroke="rgba(34,197,94,0.85)" stroke-width="1.5" stroke-dasharray="6 4" :x="mask.innerX" :y="mask.innerY" :width="mask.innerW" :height="mask.innerH" :rx="mask.innerRx" :ry="mask.innerRx" />
        </svg>

        <transition name="camera-fade">
          <div v-if="showHints" class="guide-text">{{ guideText }}</div>
        </transition>

        <transition name="camera-fade">
          <div v-if="showHints" class="camera-hint camera-hint--inner" :style="innerHintStyle">
            <span class="hint-label">Enquadre o estojo preto aqui</span>
            <img :src="arrowAsset" alt="" class="hint-arrow hint-arrow--left" />
          </div>
        </transition>

        <transition name="camera-fade">
          <div v-if="showHints && isAiMode" class="camera-hint camera-hint--outer" :style="outerHintStyle">
            <span class="hint-label">Posicione o estojo preto em um fundo claro, como uma folha A4</span>
            <img :src="arrowAsset" alt="" class="hint-arrow hint-arrow--left" />
          </div>
        </transition>

        <transition name="camera-fade">
          <div v-if="showHints && !isAiMode" class="camera-hint camera-hint--strip" :style="stripHintStyle">
            <img :src="arrowAsset" alt="" class="hint-arrow hint-arrow--small hint-arrow--up" />
            <span class="hint-label">Enquadre a tira de teste (parte branca) aqui</span>
          </div>
        </transition>

        <div class="capture-bar">
          <button
            class="capture-button"
            :class="{ 'is-pulsing': showHints }"
            type="button"
            title="Tirar foto"
            :aria-label="'Tirar foto'"
            @click="capturePhotos"
          ></button>
        </div>

        <transition name="flash">
          <div v-if="flashing" class="camera-flash" aria-hidden="true"></div>
        </transition>
      </section>

      <section v-if="step === 'review'" class="review-stage">
        <header class="review-stage__header">
          <span class="review-stage__eyebrow">Revisão da captura</span>
          <h2>Confira a imagem antes de continuar</h2>
        </header>

        <div v-if="isAiMode" class="review-images review-images--ai">
          <span class="review-label">Imagem com margem enviada para a IA</span>
          <img :src="photos.aiPreview" alt="Imagem com margem enviada para IA">
        </div>

        <div v-else class="review-images review-images--manual">
          <div class="review-tabs" role="tablist">
            <button
              v-for="tab in reviewTabs"
              :key="tab.key"
              type="button"
              role="tab"
              :aria-selected="activeReviewTab === tab.key"
              :class="['review-tabs__item', { 'is-active': activeReviewTab === tab.key }]"
              @click="activeReviewTab = tab.key"
            >
              {{ tab.label }}
            </button>
          </div>
          <transition name="camera-fade" mode="out-in">
            <img
              :key="activeReviewTab"
              :src="photos[activeReviewTab]"
              :alt="reviewTabLabel"
            />
          </transition>
          <p class="review-stage__hint">{{ reviewTabHint }}</p>
        </div>

        <div class="review-bar">
          <v-btn class="review-bar__btn review-bar__btn--retake" large @click="retake">
            <v-icon left>mdi-camera-retake-outline</v-icon>
            Tirar outra
          </v-btn>
          <v-btn class="review-bar__btn review-bar__btn--accept" large @click="acceptPhoto">
            <v-icon left>mdi-check</v-icon>
            Usar esta foto
          </v-btn>
        </div>
      </section>

      <canvas ref="canvasOuter" class="hidden-canvas"></canvas>
      <canvas ref="canvasFull" class="hidden-canvas"></canvas>
      <canvas ref="canvasInner" class="hidden-canvas"></canvas>
      <canvas ref="canvasCenter" class="hidden-canvas"></canvas>
    </div>
  </v-dialog>
</template>

<script>
import { dataUriToFile } from "./visionUtils";

const ASPECT_W = 73.1;
const ASPECT_H = 17.3;
const INNER_W = 32.1;
const INNER_H = 3.3;
const INNER_X = 35.3;
const AI_CAPTURE_MARGIN = 0.30;

// Fração do eixo longo da máscara (agora vertical) em relação à dimensão visível.
function getMaskLongPercent() {
  return window.innerHeight > window.innerWidth ? 0.55 : 0.70;
}

export default {
  name: "VisionCameraCapture",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "manual",
    },
  },

  data() {
    return {
      step: "start",
      stream: null,
      starting: false,
      errorMessage: "",
      photos: {},
      flashing: false,
      showHints: false,
      activeReviewTab: "outer",
      arrowAsset: require("@/assets/arrow.svg"),
      maskId: `vision-camera-cutout-${Math.random().toString(36).slice(2)}`,
      mask: {
        screenW: 0,
        screenH: 0,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        rx: 0,
        innerX: 0,
        innerY: 0,
        innerW: 0,
        innerH: 0,
        innerRx: 0,
      },
    };
  },

  computed: {
    isAiMode() {
      return this.mode === "ai";
    },

    title() {
      return this.isAiMode ? "Captura com IA" : "Captura manual";
    },

    startText() {
      return this.isAiMode
        ? "Posicione a tira de teste dentro da área destacada para enviar a foto ao detector automático."
        : "Posicione a tira de teste dentro da área destacada para capturar uma foto com o enquadramento correto.";
    },

    guideText() {
      return this.isAiMode ? "Enquadre a tira para a análise com IA" : "Enquadre a tira dentro da área destacada";
    },

    reviewTabs() {
      return [
        { key: "outer", label: "Foto completa", hint: "Foto completa do estojo enquadrado." },
        { key: "inner", label: "Recorte interno", hint: "Recorte da tira (parte branca)." },
        { key: "center", label: "Recorte central", hint: "Área que será enviada para análise." },
      ];
    },

    reviewTabLabel() {
      const tab = this.reviewTabs.find((t) => t.key === this.activeReviewTab);
      return tab ? tab.label : "";
    },

    reviewTabHint() {
      const tab = this.reviewTabs.find((t) => t.key === this.activeReviewTab);
      return tab ? tab.hint : "";
    },

    innerHintStyle() {
      if (!this.mask.width) return { display: "none" };
      // À esquerda da máscara vertical, centralizada na altura.
      return {
        top: `${this.mask.y + this.mask.height / 2}px`,
        left: `${Math.max(8, this.mask.x - 16)}px`,
      };
    },

    outerHintStyle() {
      if (!this.mask.width) return { display: "none" };
      // À direita da máscara vertical, centralizada na altura.
      return {
        top: `${this.mask.y + this.mask.height / 2}px`,
        left: `${Math.min(this.mask.screenW - 8, this.mask.x + this.mask.width + 16)}px`,
      };
    },

    stripHintStyle() {
      if (!this.mask.innerW) return { display: "none" };
      return {
        top: `${this.mask.innerY + this.mask.innerH + 8}px`,
        left: `${this.mask.innerX + this.mask.innerW / 2}px`,
      };
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.resetState();
        this.$nextTick(this.updateMask);
      } else {
        this.stopCamera();
      }
    },
  },

  mounted() {
    window.addEventListener("resize", this.updateMask);
    if (this.value) this.$nextTick(this.updateMask);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.updateMask);
    this.stopCamera();
  },

  methods: {
    handleDialogInput(open) {
      if (!open) this.close();
    },

    resetState() {
      this.step = "start";
      this.errorMessage = "";
      this.photos = {};
      this.starting = false;
      this.flashing = false;
      this.showHints = false;
      this.activeReviewTab = "outer";
    },

    close() {
      this.stopCamera();
      this.$emit("input", false);
    },

    showError(message) {
      this.errorMessage = message;
      this.step = "start";
      this.$emit("error", message);
    },

    updateMask() {
      const w = window.innerWidth || 0;
      const h = window.innerHeight || 0;
      // Máscara vertical: o eixo longo do estojo fica na vertical (altura).
      let rectH = h * getMaskLongPercent();
      let rectW = rectH * (ASPECT_H / ASPECT_W);

      if (rectW > w * 0.7) {
        rectW = w * 0.7;
        rectH = rectW * (ASPECT_W / ASPECT_H);
      }

      const rx = Math.min(rectW * 0.15, 18);
      const x = (w - rectW) / 2;
      const y = (h - rectH) / 2;
      const scale = rectH / ASPECT_W;
      const innerH = INNER_W * scale;
      const innerW = INNER_H * scale;
      const innerX = x + (rectW - innerW) / 2;
      const innerY = y + INNER_X * scale;
      const innerRx = Math.min(innerW * 0.2, 6);

      this.mask = {
        screenW: w,
        screenH: h,
        x,
        y,
        width: rectW,
        height: rectH,
        rx,
        innerX,
        innerY,
        innerW,
        innerH,
        innerRx,
      };
    },

    async startCamera() {
      this.errorMessage = "";

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.showError("Seu navegador não suporta acesso à câmera. Tente usar um navegador mais recente.");
        return;
      }

      this.starting = true;

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        const video = this.$refs.video;
        video.srcObject = this.stream;
        await video.play();

        this.step = "camera";
        this.showHints = false;
        this.$nextTick(() => {
          this.updateMask();
          setTimeout(() => {
            this.showHints = true;
          }, 220);
        });
      } catch (err) {
        this.stopCamera();

        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          this.showError("Permissão de acesso à câmera foi negada. Libere a câmera nas configurações do navegador e tente novamente.");
        } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
          this.showError("Nenhuma câmera encontrada no dispositivo.");
        } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
          this.showError("A câmera está sendo usada por outro aplicativo. Feche outros apps que possam estar usando a câmera e tente novamente.");
        } else {
          this.showError(`Erro ao acessar a câmera: ${err.message}`);
        }
      } finally {
        this.starting = false;
      }
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      if (this.$refs.video) {
        this.$refs.video.srcObject = null;
      }
    },

    triggerFlash() {
      this.flashing = true;
      setTimeout(() => {
        this.flashing = false;
      }, 180);
    },

    getMaskCropRect(videoWidth, videoHeight) {
      const video = this.$refs.video;
      const displayW = video.clientWidth || window.innerWidth;
      const displayH = video.clientHeight || window.innerHeight;
      const videoRatio = videoWidth / videoHeight;
      const displayRatio = displayW / displayH;
      const longPct = getMaskLongPercent();
      let x;
      let y;
      let width;
      let height;

      // Máscara vertical: o eixo longo (altura) define o tamanho do recorte.
      if (videoRatio > displayRatio) {
        const visibleW = videoHeight * displayRatio;
        const offsetX = (videoWidth - visibleW) / 2;
        height = videoHeight * longPct;
        width = height * (ASPECT_H / ASPECT_W);
        x = offsetX + (visibleW - width) / 2;
        y = (videoHeight - height) / 2;
      } else {
        const visibleH = videoWidth / displayRatio;
        const offsetY = (videoHeight - visibleH) / 2;
        height = visibleH * longPct;
        width = height * (ASPECT_H / ASPECT_W);
        x = (videoWidth - width) / 2;
        y = offsetY + (visibleH - height) / 2;
      }

      return { x, y, width, height };
    },

    clampCropRect(rect, maxW, maxH) {
      const x1 = Math.max(0, rect.x);
      const y1 = Math.max(0, rect.y);
      const x2 = Math.min(maxW, rect.x + rect.width);
      const y2 = Math.min(maxH, rect.y + rect.height);

      return {
        x: x1,
        y: y1,
        width: Math.max(1, x2 - x1),
        height: Math.max(1, y2 - y1),
      };
    },

    expandCropRect(rect, margin) {
      return {
        x: rect.x - rect.width * margin,
        y: rect.y - rect.height * margin,
        width: rect.width * (1 + margin * 2),
        height: rect.height * (1 + margin * 2),
      };
    },

    captureRectToDataUrl(sourceRect, outputCanvas) {
      const video = this.$refs.video;
      const sx = Math.round(sourceRect.x);
      const sy = Math.round(sourceRect.y);
      const sw = Math.round(sourceRect.width);
      const sh = Math.round(sourceRect.height);

      outputCanvas.width = sw;
      outputCanvas.height = sh;
      const ctx = outputCanvas.getContext("2d");
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
      return outputCanvas.toDataURL("image/jpeg", 0.92);
    },

    captureRectToDataUrlRotated(sourceRect, outputCanvas) {
      const video = this.$refs.video;
      const sx = Math.round(sourceRect.x);
      const sy = Math.round(sourceRect.y);
      const sw = Math.round(sourceRect.width);
      const sh = Math.round(sourceRect.height);

      // A máscara é vertical; giramos 90° no sentido anti-horário para que a
      // tira volte a ficar horizontal, como a análise (por colunas) espera.
      outputCanvas.width = sh;
      outputCanvas.height = sw;
      const ctx = outputCanvas.getContext("2d");
      ctx.save();
      ctx.translate(0, sw);
      ctx.rotate(-Math.PI / 2);
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
      ctx.restore();
      return outputCanvas.toDataURL("image/jpeg", 0.92);
    },

    capturePhotos() {
      const video = this.$refs.video;
      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;

      if (!videoWidth || !videoHeight) return;

      this.triggerFlash();

      const maskRect = this.getMaskCropRect(videoWidth, videoHeight);

      if (this.isAiMode) {
        const aiRect = this.clampCropRect(this.expandCropRect(maskRect, AI_CAPTURE_MARGIN), videoWidth, videoHeight);
        this.photos = {
          // Enviada ao servidor: girada para horizontal (como a análise espera).
          ai: this.captureRectToDataUrlRotated(aiRect, this.$refs.canvasFull),
          // Apenas exibição na revisão: mesma captura na orientação vertical.
          aiPreview: this.captureRectToDataUrl(aiRect, this.$refs.canvasOuter),
        };
        this.activeReviewTab = "outer";
        this.step = "review";
        return;
      }

      const outerSrcX = maskRect.x;
      const outerSrcY = maskRect.y;
      const outerSrcW = maskRect.width;
      const outerSrcH = maskRect.height;
      const outerCanvas = this.$refs.canvasOuter;
      outerCanvas.width = Math.round(outerSrcW);
      outerCanvas.height = Math.round(outerSrcH);
      outerCanvas.getContext("2d").drawImage(
        video,
        Math.round(outerSrcX),
        Math.round(outerSrcY),
        Math.round(outerSrcW),
        Math.round(outerSrcH),
        0,
        0,
        outerCanvas.width,
        outerCanvas.height
      );
      const outerDataUrl = outerCanvas.toDataURL("image/jpeg", 0.92);
      const pxPerUnit = outerSrcW / ASPECT_W;
      const innerSrcW = INNER_W * pxPerUnit;
      const innerSrcH = INNER_H * pxPerUnit;
      const innerSrcX = outerSrcX + INNER_X * pxPerUnit;
      const innerSrcY = outerSrcY + (outerSrcH - innerSrcH) / 2;
      const innerCanvas = this.$refs.canvasInner;
      innerCanvas.width = Math.round(innerSrcW);
      innerCanvas.height = Math.round(innerSrcH);
      innerCanvas.getContext("2d").drawImage(
        video,
        Math.round(innerSrcX),
        Math.round(innerSrcY),
        Math.round(innerSrcW),
        Math.round(innerSrcH),
        0,
        0,
        innerCanvas.width,
        innerCanvas.height
      );
      const innerDataUrl = innerCanvas.toDataURL("image/jpeg", 0.92);
      const centerCropW = innerSrcW * 0.618;
      const centerCropH = innerSrcH * 0.5;
      const centerCropY = innerSrcY + innerSrcH * 0.25;
      const centerCanvas = this.$refs.canvasCenter;
      centerCanvas.width = Math.round(centerCropW);
      centerCanvas.height = Math.round(centerCropH);
      centerCanvas.getContext("2d").drawImage(
        video,
        Math.round(innerSrcX),
        Math.round(centerCropY),
        Math.round(centerCropW),
        Math.round(centerCropH),
        0,
        0,
        centerCanvas.width,
        centerCanvas.height
      );

      this.photos = {
        outer: outerDataUrl,
        inner: innerDataUrl,
        center: centerCanvas.toDataURL("image/jpeg", 0.92),
      };
      this.activeReviewTab = "outer";
      this.step = "review";
    },

    retake() {
      this.photos = {};
      this.step = "camera";
      this.showHints = false;
      this.$nextTick(() => {
        this.updateMask();
        setTimeout(() => {
          this.showHints = true;
        }, 180);
      });
    },

    acceptPhoto() {
      const dataUrl = this.isAiMode ? this.photos.ai : this.photos.center;
      if (!dataUrl) {
        this.showError("Não foi possível recuperar a foto capturada.");
        return;
      }

      const suffix = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = this.isAiMode ? `camera-ai-${suffix}.jpg` : `camera-manual-${suffix}.jpg`;
      const file = dataUriToFile(dataUrl, filename);

      const originalDataUrl = this.isAiMode ? this.photos.ai : this.photos.outer;
      const originalFilename = this.isAiMode ? `camera-ai-original-${suffix}.jpg` : `camera-manual-original-${suffix}.jpg`;
      const originalFile = originalDataUrl ? dataUriToFile(originalDataUrl, originalFilename) : file;

      this.$emit("captured", { file, originalFile, dataUrl, mode: this.mode });
      this.close();
    },
  },
};
</script>

<style>
@import "./visionDesignTokens.css";
</style>

<style scoped>
.vision-camera {
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: radial-gradient(circle at 30% 20%, #111827 0%, #050505 70%);
  color: #ffffff;
}

.camera-close {
  position: fixed;
  top: 14px;
  right: 14px;
  z-index: 20;
  background: rgba(0, 0, 0, 0.4) !important;
  backdrop-filter: blur(6px);
}

/* ===== Start screen ===== */
.camera-start {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}

.camera-start__panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
  max-width: 460px;
  padding: 36px 32px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(12px);
}

.camera-start__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(70, 118, 212, 0.85) 0%, rgba(70, 118, 212, 0.45) 100%);
  box-shadow: 0 12px 28px rgba(70, 118, 212, 0.45);
}

.camera-start__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.6);
}

.camera-start__panel h2 {
  font-size: 24px;
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.2px;
}

.camera-start__panel p {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.5;
  font-size: 14.5px;
}

.camera-start__button {
  margin-top: 6px;
  color: #ffffff !important;
  background: linear-gradient(135deg, #4676D4 0%, #2f5dba 100%) !important;
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 15px !important;
  height: 50px !important;
  padding: 0 24px !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 28px rgba(70, 118, 212, 0.4) !important;
}

.camera-start__error {
  width: 100%;
  border-radius: 10px !important;
  margin-top: 6px;
}

/* ===== Camera stage ===== */
.camera-stage {
  position: fixed;
  inset: 0;
  background: #000000;
}

.camera-stage video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mask-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.guide-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  padding: 22px 56px 46px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  background: linear-gradient(rgba(0, 0, 0, 0.6), transparent);
  pointer-events: none;
}

.camera-hint {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  z-index: 5;
  max-width: min(86vw, 360px);
  text-align: center;
}

/* Dica à esquerda da máscara: rótulo + seta apontando para a direita. */
.camera-hint--inner {
  flex-direction: row;
  align-items: center;
  gap: 8px;
  transform: translate(-100%, -50%);
  text-align: right;
  max-width: min(40vw, 220px);
}

/* Dica à direita da máscara: seta apontando para a esquerda + rótulo. */
.camera-hint--outer {
  flex-direction: row-reverse;
  align-items: center;
  gap: 8px;
  transform: translateY(-50%);
  text-align: left;
  max-width: min(40vw, 220px);
}

.camera-hint--strip {
  transform: translateX(-50%);
}

.hint-arrow {
  width: auto;
  height: 12vh;
  filter: brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0, 0, 0, 0.6));
}

/* Seta base ("left-arrow") aponta para a esquerda em 0°. */
.camera-hint--inner .hint-arrow,
.camera-hint--outer .hint-arrow {
  height: 9vh;
}

.hint-arrow--right {
  transform: rotate(180deg);
}

.hint-arrow--left {
  transform: rotate(0deg);
}

.hint-arrow--up {
  transform: rotate(90deg);
}

.hint-arrow--small {
  height: 8vh;
  transform: rotate(0deg) !important;
}

.hint-label {
  background: rgba(0, 0, 0, 0.82);
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
  padding: 8px 12px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

/* ===== Capture button ===== */
.capture-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  padding: 24px 0 40px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
}

.capture-button {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 4px solid #ffffff;
  background: rgba(255, 255, 255, 0.18);
  cursor: pointer;
  transition: transform 0.1s ease;
}

.capture-button.is-pulsing {
  animation: vision-pulse-ring 2s ease-out infinite;
}

.capture-button::after {
  content: "";
  position: absolute;
  inset: 6px;
  border-radius: 50%;
  background: #ffffff;
  transition: transform 0.1s;
}

.capture-button:active {
  transform: scale(0.96);
}

.capture-button:active::after {
  transform: scale(0.9);
}

/* ===== Flash ===== */
.camera-flash {
  position: absolute;
  inset: 0;
  background: #ffffff;
  pointer-events: none;
  z-index: 30;
}

.flash-enter-active {
  transition: opacity 80ms ease-out;
}

.flash-leave-active {
  transition: opacity 200ms ease-in;
}

.flash-enter,
.flash-leave-to {
  opacity: 0;
}

/* ===== Hints fade ===== */
.camera-fade-enter-active,
.camera-fade-leave-active {
  transition: opacity 380ms ease, transform 380ms ease;
}

.camera-fade-enter,
.camera-fade-leave-to {
  opacity: 0;
}

/* ===== Review stage ===== */
.review-stage {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #000000;
  padding: 18px 18px 0;
}

.review-stage__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  padding: 12px 0 4px;
}

.review-stage__eyebrow {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.55);
}

.review-stage__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.1px;
}

.review-images {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 4px;
}

.review-images img {
  max-width: 96%;
  max-height: 56vh;
  object-fit: contain;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
}

.review-images--ai img {
  max-height: 68vh;
}

.review-label {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 700;
}

.review-tabs {
  display: inline-flex;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  padding: 4px;
  gap: 2px;
}

.review-tabs__item {
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12.5px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 999px;
  cursor: pointer;
  transition: background 180ms ease, color 180ms ease;
  font-family: inherit;
}

.review-tabs__item:hover {
  color: #fff;
}

.review-tabs__item.is-active {
  background: #4676D4;
  color: #fff;
  box-shadow: 0 4px 14px rgba(70, 118, 212, 0.45);
}

.review-stage__hint {
  margin: 0;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12.5px;
  text-align: center;
}

.review-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 18px 0 28px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
}

.review-bar__btn {
  text-transform: none !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  height: 48px !important;
  padding: 0 22px !important;
  border-radius: 12px !important;
}

.review-bar__btn--retake {
  background: rgba(255, 255, 255, 0.1) !important;
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.review-bar__btn--accept {
  background: linear-gradient(135deg, #198754 0%, #14b061 100%) !important;
  color: #fff !important;
  box-shadow: 0 12px 24px rgba(25, 135, 84, 0.4) !important;
}

.hidden-canvas {
  display: none;
}

@media (max-width: 480px) {
  .review-tabs__item {
    padding: 8px 12px;
    font-size: 11.5px;
  }

  .review-bar__btn {
    padding: 0 16px !important;
    font-size: 13px !important;
  }
}
</style>
